import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Clapperboard, Plus, Loader2, Search, Trash2, X, ImageIcon, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { adCreativesAPI } from '../lib/api';

const fmtDate = (s) => {
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return ''; }
};

export default function AdCreatives() {
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adCreativesAPI.listSets();
      setSets(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load sets');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this ad set and all its media?')) return;
    try {
      await adCreativesAPI.deleteSet(id);
      setSets((prev) => prev.filter((s) => s.id !== id));
      toast.success('Set deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="ad-creatives-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900 inline-flex items-center gap-2.5" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Clapperboard className="w-7 h-7 text-orange-600" /> Ad Creatives
          </h1>
          <p className="text-sm text-zinc-500 mt-1.5">
            Build sets of ad media from a listing, then generate high-conversion Meta ad copy with AI.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-set-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Create Set
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-zinc-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : sets.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-200 rounded-2xl py-20 text-center" data-testid="ad-creatives-empty">
          <Clapperboard className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-600 font-medium">No ad sets yet</p>
          <p className="text-zinc-400 text-sm mt-1">Create your first set and pick a listing to get started.</p>
          <Button onClick={() => setCreateOpen(true)} className="mt-5 bg-orange-600 hover:bg-orange-700 text-white" data-testid="create-set-empty-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Create Set
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ad-sets-grid">
          {sets.map((s) => (
            <div
              key={s.id}
              onClick={() => navigate(`/ad-creatives/${s.id}`)}
              className="group bg-white border border-zinc-200 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg hover:border-orange-200 transition-all"
              data-testid={`ad-set-card-${s.id}`}
            >
              <div className="aspect-[4/3] bg-zinc-100 relative overflow-hidden">
                {s.thumbnail_url ? (
                  <img src={s.thumbnail_url} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-300"><ImageIcon className="w-10 h-10" /></div>
                )}
                <button
                  onClick={(e) => handleDelete(e, s.id)}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-md text-zinc-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                  data-testid={`delete-set-${s.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[11px] font-medium bg-black/60 text-white rounded-full px-2 py-0.5">
                  <Layers className="w-3 h-3" /> {s.media_count} media
                </span>
              </div>
              <div className="p-3.5">
                <h3 className="text-sm font-semibold text-zinc-900 truncate">{s.name}</h3>
                <p className="text-xs text-zinc-500 truncate mt-0.5">{s.listing_title}</p>
                <p className="text-[11px] text-zinc-400 mt-2">{s.created_by_name} · {fmtDate(s.updated_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {createOpen && <CreateSetModal onClose={() => setCreateOpen(false)} onCreated={(id) => navigate(`/ad-creatives/${id}`)} />}
    </div>
  );
}

function CreateSetModal({ onClose, onCreated }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const search = useCallback(async (query) => {
    try {
      setLoading(true);
      const res = await adCreativesAPI.listListings(query);
      setListings(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load listings');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { search(''); }, [search]);
  useEffect(() => {
    const t = setTimeout(() => search(q), 350);
    return () => clearTimeout(t);
  }, [q, search]);

  const handleCreate = async () => {
    if (!selected) { toast.error('Pick a listing first'); return; }
    try {
      setCreating(true);
      const res = await adCreativesAPI.createSet({ listing_id: selected.id, name: name.trim() || undefined });
      toast.success('Set created');
      onCreated(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="create-set-modal-backdrop">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="create-set-modal">
        <div className="flex items-center justify-between p-5 border-b border-zinc-100">
          <h2 className="text-lg font-semibold text-zinc-900">Create ad set — choose a listing</h2>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="create-set-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 border-b border-zinc-100">
          <div className="relative">
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your listings…" className="pl-9" data-testid="listing-search-input" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : listings.length === 0 ? (
            <p className="text-center text-zinc-400 py-16 text-sm">No listings found.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="listing-picker-grid">
              {listings.map((l) => (
                <button
                  key={l.id}
                  onClick={() => { setSelected(l); if (!name) setName(l.title); }}
                  className={`text-left border rounded-lg overflow-hidden transition-all ${selected?.id === l.id ? 'border-orange-500 ring-2 ring-orange-200' : 'border-zinc-200 hover:border-orange-300'}`}
                  data-testid={`listing-option-${l.id}`}
                >
                  <div className="aspect-square bg-zinc-100 overflow-hidden">
                    {l.thumbnail_url
                      ? <img src={l.thumbnail_url} alt={l.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full flex items-center justify-center text-zinc-300"><ImageIcon className="w-8 h-8" /></div>}
                  </div>
                  <p className="text-[11px] text-zinc-700 p-2 line-clamp-2 leading-snug">{l.title}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-zinc-100 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500 mb-1 block">Set name (optional)</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Spring Cushion Ring Campaign" className="text-sm" data-testid="set-name-input" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={onClose} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !selected} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="confirm-create-set-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Create set
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
